import React, { useCallback } from 'react';

import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import SectionMessage from '@atlaskit/section-message';

import integration from 'src/lib/api/integration';

import ErrorMessage from 'src/views/components/ErrorMessage';
import Field from 'src/views/components/Form/Field';
import { FormProvider, useForm } from 'src/views/components/Form/form';
import { JiraUserSelect, PdUsersSelect } from 'src/views/components/select';

const ConfigForm = () => {
  const { values,
    name,
    isSubmitting,
    isSubmitSuccessful,
    submitError,
    resetToInitialState,
    anyTouched,
    onChange,
    onBlur,
    onSubmit,
    loadingInitValues,
    initValuesLoadingError } = useForm();

  if (loadingInitValues) {
    return null;
  }

  if (initValuesLoadingError) {
    return <ErrorMessage error={['Failed to load user mapping config', initValuesLoadingError]}/>;
  }

  return (
    <form onSubmit={onSubmit}>
      <Heading size="medium">User mapping configuration</Heading>
      <div style={{ display: 'flex' }}>
        <div style={{ width: '50%' }}>
          <div>
            <Field
              label="Fallback PagerDuty integration user"
              helperMessage="Used when a user isn’t mapped, but is required by the PagerDuty API  (e.g. Creator of an incident)"
            >
              <PdUsersSelect
                isClearable
                value={values.pd_fallback_user || null}
                onChange={onChange}
                onBlur={onBlur}
                name={`${name}.pd_fallback_user`}
              />
            </Field>
            { values.pd_fallback_user ? null : <SectionMessage appearance="warning">
              PagerDuty incident operation will be aborted if Jira user, who is performing an action, is not mapped to PagerDuty user.
            </SectionMessage> }
          </div>
        </div>
        <div style={{ width: '20px' }}/>
        <div style={{ width: '50%' }}>
          <div>
            <Field
              label="Fallback Jira integration user"
              helperMessage="Used to populate required user field, when there is no user mapping"
            >
              <JiraUserSelect
                isClearable
                value={values.jira_fallback_user || null}
                onChange={onChange}
                onBlur={onBlur}
                name={`${name}.jira_fallback_user`}
              />
            </Field>
            { values.jira_fallback_user ? null : <SectionMessage appearance="warning">
              Jira issue operation will be aborted if PagerDuty user, who is performing an action, is not mapped to Jira user.
            </SectionMessage> }
          </div>
        </div>
      </div>

      {submitError && <ErrorMessage error={['Failed to update User mapping settings', submitError]}/>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '20px 0' }}>
        <ButtonGroup>
          <Button isDisabled={!anyTouched} onClick={() => {
            if (document.activeElement) document.activeElement.blur();
            resetToInitialState();
          }}>Cancel</Button>
          <LoadingButton appearance="primary" type="submit" isDisabled={!anyTouched} isLoading={isSubmitting}>{isSubmitSuccessful && !anyTouched ? 'Successfully saved!' : 'Save'}</LoadingButton>
        </ButtonGroup>
      </div>
    </form>
  );
};

const ConfigFormWrapper = () => {
  const initValues = useCallback(async () => {
    const { users_mapping_config } = await integration.getAccountMapping();
    return users_mapping_config || {};
  }, []);

  const onSave = useCallback(users_mapping_config => integration.updateAccountsMapping({ users_mapping_config }), []);

  return (
    <FormProvider initValues={initValues} onSave={onSave}>
      <ConfigForm/>
    </FormProvider>
  );
};

export default ConfigFormWrapper;
